/**
 * Informe de Matrículas Cobradas — migración web de ares6020.
 *
 * Listado de cobros de matrícula por rango de fechas, cliente o chasis
 * (criterios excluyentes, como el escritorio), con export a PDF, totales y
 * reimpresión del recibo por fila. Se abre desde Cobros de Matrícula
 * (matriculascobros.js) en un diálogo a pantalla completa.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import CancelIcon from "@mui/icons-material/Cancel";
import DescriptionIcon from "@mui/icons-material/Description";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, StatCard, FpDataGrid, useToast, fpInputSx } from "components/financepro";
import { useApi, usePerfilSucursal, useSucursales } from "lib/api";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { imprimirReciboMatricula } from "modules/matriculas/cobromatricula";
import { labelTipo } from "lib/documentosvehiculo";

const NAVY = "#18586b";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// El rango del informe filtra por fecha del cobro de la matrícula; en las
// financiadas, "cobrado" es acumulado a la fecha (lo que el cliente ya pagó
// dentro de sus cuotas), no lo facturado en ese rango.
const AVISO_FINANCIADAS =
  "El rango filtra por fecha del cobro de la matrícula. En las financiadas en cuotas, " +
  '"cobrado" es lo que el cliente ha pagado hasta hoy dentro de sus cuotas, no lo facturado.';

const AVISO_COBRADO =
  "El rango filtra por fecha en que entró el dinero: contado en ventanilla, más lo que " +
  "el cliente pagó del documento dentro de sus cuotas o de un abono a capital. No incluye " +
  "los saldos de apertura migrados.";

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.4, fontWeight: 700, boxShadow: "none" };

// Columnas del PDF por modo. El eje de fecha cambia la pregunta y con ella
// las columnas: en "facturado" la fila es un cobro de documento; en "cobrado"
// es un movimiento de dinero (recibo de cuota, abono o contado).
const COLS_PDF = {
  facturado: [
    { h: "Prest#", w: 48, get: (r) => r.noprest },
    { h: "Fecha", w: 62, get: (r) => r.fecha1 },
    { h: "Cliente", flex: 1.4, get: (r) => [r.nombres, r.apellidos].filter(Boolean).join(" ") },
    { h: "Cédula", w: 78, get: (r) => r.cedula },
    { h: "Vehículo", flex: 1, get: (r) => [r.marca, r.modelo, r.colore].filter(Boolean).join(" ") },
    { h: "Chasis", w: 108, get: (r) => r.chasis },
    { h: "Documento", w: 66, get: (r) => (r.tipodocumento ? labelTipo(r.tipodocumento) : "") },
    { h: "Valor", w: 62, right: true, get: (r) => fmtMoney(r.valor) },
  ],
  cobrado: [
    { h: "Fecha pago", w: 62, get: (r) => r.fecha },
    { h: "Recibo", w: 50, get: (r) => r.norecibo || "—" },
    { h: "Prest#", w: 48, get: (r) => r.noprest },
    { h: "Cliente", flex: 1.4, get: (r) => r.cliente },
    { h: "Chasis", w: 108, get: (r) => r.chasis },
    { h: "Documento", w: 66, get: (r) => (r.tipodocumento ? labelTipo(r.tipodocumento) : "") },
    { h: "Origen", w: 78, get: (r) => r.origen },
    { h: "Monto", w: 62, right: true, get: (r) => fmtMoney(r.monto) },
  ],
};

// Reporte PDF (mismo patrón visual del reporte de motores).
function ReportePdf({ rows, nombreNegocio, isXs, modo }) {
  const cols = COLS_PDF[modo] || COLS_PDF.facturado;
  const campoValor = modo === "cobrado" ? "monto" : "valor";
  const total = rows.reduce((sum, r) => sum + toNum(r[campoValor]), 0);
  const th = { padding: 4, fontWeight: "bold" };
  const anchoCol = (c) => (c.flex ? { flex: c.flex } : { width: c.w });
  return (
    <PDFViewer
      style={{
        height: isXs ? "calc(100vh - 120px)" : "550px",
        width: "100%",
        colorScheme: "light",
        background: "#fff",
      }}
    >
      <Document>
        <Page size="LETTER" orientation="portrait" wrap style={{ backgroundColor: "#FFFFFF", padding: 24, paddingBottom: 48 }}>
          <View style={{ textAlign: "center", fontSize: 12, textTransform: "uppercase" }} fixed>
            <Text>{nombreNegocio || ""}</Text>
          </View>
          <View style={{ textAlign: "center", fontSize: 9, textTransform: "uppercase", marginBottom: 10 }} fixed>
            <Text>
              {modo === "cobrado"
                ? "Documentos cobrados en el período"
                : "Reporte de Matrículas Cobradas"}
            </Text>
          </View>
          <View
            style={{ flexDirection: "row", fontSize: 8, backgroundColor: NAVY, color: "#FFFFFF", paddingVertical: 4 }}
            fixed
          >
            {cols.map((c) => (
              <Text
                key={c.h}
                style={{ ...th, ...anchoCol(c), textAlign: c.right ? "right" : "left" }}
              >
                {c.h}
              </Text>
            ))}
          </View>
          {rows.map((r, i) => (
            <View
              key={r.id ?? i}
              style={{
                flexDirection: "row",
                fontSize: 8,
                color: "#1a2430",
                paddingVertical: 3,
                backgroundColor: i % 2 === 0 ? "#f5f8fa" : "#FFFFFF",
              }}
              wrap={false}
            >
              {cols.map((c) => (
                <Text
                  key={c.h}
                  style={{ padding: 4, ...anchoCol(c), textAlign: c.right ? "right" : "left" }}
                >
                  {c.get(r)}
                </Text>
              ))}
            </View>
          ))}
          <View
            style={{
              flexDirection: "row",
              fontSize: 9,
              fontWeight: "bold",
              color: "#1a2430",
              borderTopWidth: 2,
              borderTopColor: NAVY,
              paddingVertical: 5,
              marginTop: 2,
            }}
          >
            <Text style={{ padding: 4, flex: 1 }}>
              Total — {rows.length} {modo === "cobrado" ? "movimientos" : "cobros"}
            </Text>
            <Text style={{ padding: 4, width: 90, textAlign: "right" }}>{fmtMoney(total)}</Text>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
}

export default function InformeMatriculasCobradas() {
  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleDetalles } = controller;
  const { post } = useApi();
  // Alcance de sucursal: tipo admin, permiso global `versucursales` o el
  // checkbox "Ver sucursal" del informe (ares6020) en el rol. El backend
  // fuerza lo mismo en /api/informecobrosmatriculas; esto lo refleja en el
  // filtro.
  const { esadmin, sucursalPropia } = usePerfilSucursal();
  const veSucursales = esadmin || puedeAccionPrograma(roleDetalles, "ares6020", "versucursal");
  const sucursales = useSucursales({ todas: veSucursales });
  const [nosucursal, setNosucursal] = useState(null);
  // Sin alcance multi-sucursal se preselecciona la suya (y queda fija).
  useEffect(() => {
    if (veSucursales || !sucursalPropia || !sucursales.length) return;
    const propia = sucursales.find(
      (s) =>
        String(s.label || "")
          .split("-")[0]
          .trim() === sucursalPropia
    );
    if (propia) setNosucursal((prev) => prev || propia);
  }, [veSucursales, sucursalPropia, sucursales]);
  const toast = useToast();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");
  const [filtroNombres, setFiltroNombres] = useState("");
  const [filtroChasis, setFiltroChasis] = useState("");
  const [rows, setRows] = useState([]);
  const [totales, setTotales] = useState(null);
  // Eje de fecha. "facturado": el rango filtra por fecha del cobro del
  // documento (de lo facturado, cuánto se ha recuperado). "cobrado": filtra
  // por fecha en que entró el dinero (cuánto entró en el período). Son
  // preguntas distintas: mezclarlas en una tabla es lo que hacía ilegible
  // este informe, así que se elige una.
  const [modo, setModo] = useState("facturado");
  const [cargando, setCargando] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const [presentarpdf, setPresentarpdf] = useState(false);

  const hayTexto = filtroNombres.trim() !== "" || filtroChasis.trim() !== "";
  const hayFechas = fecha1 !== "" || fecha2 !== "";

  const procesar = async () => {
    setCargando(true);
    try {
      const { status, data } = await post("/api/informecobrosmatriculas", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        nombres: filtroNombres.trim() || undefined,
        chasis: filtroChasis.trim() || undefined,
        sucursal: nosucursal && nosucursal.label ? nosucursal.label.split("-")[0] : "",
        modo,
      });
      setBuscado(true);
      if (status === 200) {
        // En modo cobrado la fila es un movimiento de dinero y no trae id
        // propio (puede haber varios del mismo cargo): lo pone la grilla.
        setRows(
          modo === "cobrado"
            ? (data.movimientos || []).map((m, i) => ({ id: i, ...m }))
            : data.matriculas || []
        );
        setTotales(data.totales || null);
      } else {
        setRows([]);
        setTotales(null);
        toast.error(data?.Error || "No se pudo cargar el informe");
      }
    } finally {
      setCargando(false);
    }
  };

  const quitarFiltros = () => {
    setFecha1("");
    setFecha2("");
    setFiltroNombres("");
    setFiltroChasis("");
    if (veSucursales) setNosucursal(null);
    setRows([]);
    setTotales(null);
    setBuscado(false);
  };

  const handlePdf = () => {
    if (rows.length === 0) {
      toast.aviso("No hay datos para procesar este reporte");
      return;
    }
    setPresentarpdf(true);
  };

  // Reimpresión del recibo de la fila (mismo recibo que ares6010/6040).
  const reimprimirFila = (row) => {
    imprimirReciboMatricula({
      fecha: row.fecha1,
      cedula: row.cedula,
      cliente: [row.nombres, row.apellidos].filter(Boolean).join(" "),
      noprest: row.noprest,
      chasis: row.chasis,
      marca: row.marca,
      modelo: row.modelo,
      colore: row.colore,
      valor: row.valor,
      razon: row.razon || row.referencia,
      documento: row.tipodocumento,
      compania: localStorage.getItem("nombrenegocio") || "",
    });
  };

  // Los totales los calcula el backend: el "cobrado" de una matrícula
  // financiada no es su valor, sino lo que el libro de asignación registra
  // que ya entró por las cuotas.
  const esCobrado = modo === "cobrado";
  const totalFacturado = totales
    ? totales.facturado
    : rows.reduce((sum, r) => sum + toNum(r.valor), 0);
  const totalCobrado = esCobrado
    ? (totales && totales.cobradoperiodo) || 0
    : (totales && totales.cobradototal) ?? totalFacturado;
  const totalPendiente = totales ? totales.pendiente : 0;
  const hayFinanciadas = !esCobrado && Boolean(totales && totales.financiado > 0);
  const porDocumento = (totales && totales.pordocumento) || [];
  // "Cobrado" significa cosas distintas segun el eje: en el periodo (dinero
  // que entro en el rango) o a la fecha (acumulado del cargo).
  let etiquetaCobrado = "Total cobrado";
  if (esCobrado) etiquetaCobrado = "Cobrado en el período";
  else if (hayFinanciadas) etiquetaCobrado = "Cobrado a la fecha";

  // Modo cobrado: la fila es un movimiento de dinero, no un cobro de
  // documento. Sin recibo cuando vino de un abono a capital o de ventanilla.
  const columnsCobrado = [
    { field: "fecha", headerName: "Fecha pago", width: 110 },
    {
      field: "norecibo",
      headerName: "Recibo",
      width: 90,
      renderCell: (params) => params.row.norecibo || "—",
    },
    { field: "noprest", headerName: "Préstamo", width: 95 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    { field: "chasis", headerName: "Chasis", flex: 1, minWidth: 180 },
    {
      field: "tipodocumento",
      headerName: "Documento",
      width: 130,
      valueFormatter: (value) => (value ? labelTipo(value) : "—"),
    },
    { field: "origen", headerName: "Origen", width: 140 },
    { field: "sucursal", headerName: "Sucursal", width: 100 },
    {
      field: "monto",
      headerName: "Monto",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
  ];

  const columns = [
    { field: "noprest", headerName: "Préstamo", width: 95 },
    { field: "fecha1", headerName: "Fecha", width: 105 },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 1,
      minWidth: 180,
      valueGetter: (_v, row) => [row.nombres, row.apellidos].filter(Boolean).join(" "),
    },
    { field: "cedula", headerName: "Cédula", width: 135 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 150,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
    { field: "chasis", headerName: "Chasis", flex: 1, minWidth: 200 },
    {
      field: "tipodocumento",
      headerName: "Documento",
      width: 130,
      valueFormatter: (value) => (value ? labelTipo(value) : "—"),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    { field: "formacobro", headerName: "Forma de cobro", width: 165 },
    { field: "sucursal", headerName: "Sucursal", width: 100 },
    {
      field: "cobrado",
      headerName: "Cobrado",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "pendiente",
      headerName: "Pendiente",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 64,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      renderCell: (params) => (
        <Tooltip title="Imprimir recibo">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              reimprimirFila(params.row);
            }}
            sx={{ color: FP.color.navy }}
          >
            <PrintOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Informe de Matrículas Cobradas"
        subtitle="Matrículas cobradas por rango de fechas, cliente o chasis"
        cta={
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            disabled={rows.length === 0}
            onClick={handlePdf}
            sx={{
              ...btnBase,
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              px: 2.2,
              height: 38,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Exportar PDF
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        {/* Filtros */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={modo}
              onChange={(e, v) => {
                if (!v || v === modo) return;
                setModo(v);
                // Cambia la pregunta: los resultados anteriores ya no aplican.
                setRows([]);
                setTotales(null);
                setBuscado(false);
              }}
              sx={{ ml: "auto", bgcolor: "#FFFFFF" }}
            >
              <ToggleButton value="facturado" sx={{ ...btnBase, px: 1.6, py: 0.4 }}>
                Facturado
              </ToggleButton>
              <ToggleButton value="cobrado" sx={{ ...btnBase, px: 1.6, py: 0.4 }}>
                Cobrado
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFecha1(v);
                    if (v) {
                      setFiltroNombres("");
                      setFiltroChasis("");
                    }
                  }}
                  disabled={hayTexto}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFecha2(v);
                    if (v) {
                      setFiltroNombres("");
                      setFiltroChasis("");
                    }
                  }}
                  disabled={hayTexto}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Buscar por Cliente"
                  placeholder="Ej. Juan Pérez"
                  value={filtroNombres}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFiltroNombres(v);
                    if (v.trim()) {
                      setFiltroChasis("");
                      setFecha1("");
                      setFecha2("");
                    }
                  }}
                  disabled={filtroChasis.trim() !== "" || hayFechas}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Buscar por Chasis"
                  placeholder="Número de chasis"
                  value={filtroChasis}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFiltroChasis(v);
                    if (v.trim()) {
                      setFiltroNombres("");
                      setFecha1("");
                      setFecha2("");
                    }
                  }}
                  disabled={filtroNombres.trim() !== "" || hayFechas}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  options={sucursales || []}
                  value={nosucursal}
                  disabled={!veSucursales}
                  getOptionLabel={(opt) => (opt && opt.label) || ""}
                  isOptionEqualToValue={(opt, val) => (opt && opt.label) === (val && val.label)}
                  onChange={(e, v) => setNosucursal(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      placeholder="Todas"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              flexWrap="wrap"
              sx={{ mt: 2.5 }}
            >
              <Button
                variant="outlined"
                startIcon={<FilterListOffIcon />}
                onClick={quitarFiltros}
                sx={{
                  ...btnBase,
                  height: 46,
                  color: FP.color.textPrimary,
                  borderColor: FP.color.border,
                  bgcolor: "#FFFFFF",
                  "&:hover": { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                }}
              >
                Quitar filtros
              </Button>
              <Button
                variant="contained"
                startIcon={
                  cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={cargando}
                sx={{
                  ...btnBase,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  fontSize: "0.94rem",
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                }}
              >
                {cargando ? "Procesando…" : "Procesar"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Stats */}
        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: hayFinanciadas ? "repeat(4, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <StatCard
              label={esCobrado ? "Movimientos" : "Matrículas cobradas"}
              value={String(rows.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            {hayFinanciadas && (
              <StatCard
                label="Facturado"
                value={`$${fmtMoney(totalFacturado)}`}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            )}
            <StatCard
              label={etiquetaCobrado}
              value={`$${fmtMoney(totalCobrado)}`}
              icon={PaymentsOutlinedIcon}
              accent={FP.color.success}
            />
            {hayFinanciadas && (
              <StatCard
                label="Pendiente en cuotas"
                value={`$${fmtMoney(totalPendiente)}`}
                icon={PaymentsOutlinedIcon}
                accent={FP.color.warning}
              />
            )}
          </Box>
        )}

        {(hayFinanciadas || esCobrado) && (
          <Typography sx={{ mb: 2, fontSize: "0.82rem", color: FP.color.textSecondary }}>
            {esCobrado ? AVISO_COBRADO : AVISO_FINANCIADAS}
          </Typography>
        )}

        {/* En esta pantalla se cobran matrícula, placa y copias: "cuánto se
            cobró de matrícula" es su línea, no el total de la pantalla. */}
        {porDocumento.length > 1 && (
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              p: 2,
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", mb: 1.2 }}>
              Por documento
            </Typography>
            <Stack spacing={0.8}>
              {porDocumento.map((d) => (
                <Stack
                  key={d.tipodocumento}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ fontSize: "0.85rem" }}
                >
                  <Typography sx={{ fontWeight: 700, minWidth: 150, fontSize: "0.85rem" }}>
                    {labelTipo(d.tipodocumento)}
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.85rem" }}>
                    {d.cantidad} cobro{d.cantidad === 1 ? "" : "s"}
                  </Typography>
                  {!esCobrado && (
                    <Typography sx={{ ml: "auto", fontSize: "0.85rem" }}>
                      Facturado ${fmtMoney(d.facturado)}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: FP.color.success,
                      fontSize: "0.85rem",
                      ml: esCobrado ? "auto" : 0,
                    }}
                  >
                    Cobrado ${fmtMoney(d.cobrado)}
                  </Typography>
                  {!esCobrado && d.pendiente > 0 && (
                    <Typography
                      sx={{ fontWeight: 700, color: FP.color.warning, fontSize: "0.85rem" }}
                    >
                      Pendiente ${fmtMoney(d.pendiente)}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          </Card>
        )}

        {/* Listado */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.successSoft || "#E8F5E9",
                color: FP.color.success,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BadgeOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Matrículas cobradas
            </Typography>
            <Typography
              sx={{ ml: "auto", color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}
            >
              {rows.length} {rows.length === 1 ? "registro" : "registros"}
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <Box sx={{ height: 520 }}>
              <FpDataGrid
                rows={rows}
                columns={esCobrado ? columnsCobrado : columns}
                loading={cargando}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                pageSizeOptions={[10, 25, 50]}
                vacioTitulo={buscado ? "Sin cobros con esos filtros" : "No hay datos para mostrar"}
                vacioSubtitulo={
                  buscado
                    ? "Pruebe con otro rango de fechas, cliente o chasis."
                    : "Seleccione los filtros y haga clic en “Procesar” para obtener resultados."
                }
              />
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Vista previa del PDF */}
      {presentarpdf && (
        <Dialog
          open={presentarpdf}
          maxWidth="lg"
          fullWidth
          fullScreen={isXs}
          onClose={() => setPresentarpdf(false)}
          PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              Vista Previa del Reporte PDF
            </Typography>
            <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
              <CancelIcon />
            </Button>
          </DialogTitle>
          <DialogContent>
            <ReportePdf rows={rows} nombreNegocio={nombreNegocio} isXs={isXs} modo={modo} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}
